import type { IClinicalQAReview } from '../../../../utils/types';

export type ClinicalQAScoreField = Exclude<
  {
    [Key in keyof IClinicalQAReview]: IClinicalQAReview[Key] extends boolean ? Key : never;
  }[keyof IClinicalQAReview],
  undefined
>;

interface ScoreConfigItem {
  field: ClinicalQAScoreField;
  label: string;
  shortLabel: string;
  dataTestId?: string;
}

export const ScoreFieldsV1: ScoreConfigItem[] = [
  {
    field: 'is_all_active_programs_used_within_7days',
    label: 'All behavior reduction goals are captured',
    shortLabel: 'All behavior reduction goals are captured',
    dataTestId: 'allActiveProgramsUsedWithin7Days',
  },
  {
    field: 'is_data_collected_per_forta_protocols',
    label: 'Data collected per Forta protocols (E)',
    shortLabel: 'Data collected per Forta protocols',
    dataTestId: 'dataCollectedAsPerFortaRecords',
  },
  {
    field: 'is_at_least_2programs_run_per_hour',
    label: 'At least 2 different programs run per hour',
    shortLabel: 'At least 2 different programs run per hour',
    dataTestId: 'isAtleast2ProgramsRunPerHour',
  },
  {
    field: 'has_10data_points_per_hour',
    label: '10 data points per hour',
    shortLabel: '10 data points per hour',
    dataTestId: 'has10DataPointsPerHour',
  },
  {
    field: 'is_note_submitted_within_48hours_of_session',
    label: 'Session notes submitted within 48 hours of the session',
    shortLabel: 'Session notes submitted within 48 hours of the session',
    dataTestId: 'isNoteSubmittedWithin48HoursOfSession',
  },
  // Column 2 fields
  {
    field: 'is_datetime_accurate',
    label: 'Accurate times and dates',
    shortLabel: 'Accurate times and dates',
    dataTestId: 'isDateTimeAccurate',
  },
  {
    field: 'is_language_objective',
    label:
      'Objective language (E)\n Not mentalistic language (e.g., client has been feeling stressed and an increase of anxiety lately) If subjective & mentalistic language is used, it should always be supported by objective data',
    shortLabel: 'Objective language - Not mentalistic language',
    dataTestId: 'isLanguageObjective',
  },
  {
    field: 'is_detail_sufficient_quantity',
    label: 'Sufficient quantity of details (E)',
    shortLabel: 'Sufficient quantity of details',
    dataTestId: 'isDetailSufficientQuantity',
  },
  {
    field: 'is_detail_sufficient_quality',
    label: 'Sufficient quality of details: (E) \nWho did what with who & where',
    shortLabel: 'Sufficient quality of details: Who did what with who & where',
    dataTestId: 'isDetailSufficientQuality',
  },
  {
    field: 'is_overall_coherent',
    label: 'Overall Coherence (E)',
    shortLabel: 'Overall Coherence',
    dataTestId: 'isOverallCoherent',
  },
];

export const ScoreFieldsV2: ScoreConfigItem[] = [
  {
    field: 'is_all_active_programs_used_within_7days',
    label: 'All Behavior Goals are Captured (E)',
    shortLabel: 'All Behavior Goals are Captured',
    dataTestId: 'allActiveProgramsUsedWithin7Days',
  },
  {
    field: 'is_at_least_2programs_run_per_hour',
    label: 'At least two different programs run per hour',
    shortLabel: 'At least two different programs run per hour',
    dataTestId: 'isAtleast2ProgramsRunPerHour',
  },
  {
    field: 'has_10data_points_per_hour',
    label: '10 data points per hour & data-narrative alignment',
    shortLabel: '10 data points per hour & data-narrative alignment',
    dataTestId: 'has10DataPointsPerHour',
  },
  {
    field: 'is_language_objective',
    label:
      'Objective language - Not mentalistic language (e.g., the client has been feeling stressed and had an increase in anxiety lately). If subjective & mentalistic language is used, it should always be supported by objective data (E)',
    shortLabel: 'Objective language - Not mentalistic language',
    dataTestId: 'isLanguageObjective',
  },
  {
    field: 'is_detail_sufficient_quality',
    label:
      'Sufficient quality of details: Who was present, where did the session take place, how did the client respond to the therapy (E)',
    shortLabel: 'Sufficient quality of details',
    dataTestId: 'isDetailSufficientQuality',
  },
  // Column 2 fields
  {
    field: 'one_setting_per_session_note',
    label: 'One setting per session note',
    shortLabel: 'One setting per session note',
    dataTestId: 'oneSettingPerSessionNote',
  },
  {
    field: 'therapist_role_aligns_with_tp_goals',
    label:
      'Therapist role aligns with TP goals: What did the therapist do in connection with goals from the TP (E)',
    shortLabel: 'Therapist role aligns with TP goals',
    dataTestId: 'therapistRoleAlignsWithTPGoal',
  },
  {
    field: 'is_datetime_accurate',
    label: 'Time and narrative alignment',
    shortLabel: 'Time and narrative alignment',
    dataTestId: 'isDateTimeAccurate',
  },
  {
    field: 'is_overall_coherent',
    label: 'Overall Coherence (E)',
    shortLabel: 'Overall Coherence',
    dataTestId: 'isOverallCoherent',
  },
  {
    field: 'is_note_submitted_within_48hours_of_session',
    label: 'Session note submitted within 48 hours of the session',
    shortLabel: 'Session note submitted within 48 hours of the session',
    dataTestId: 'isNoteSubmittedWithin48HoursOfSession',
  },
];
